import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';
import { Target, Phone, MessageSquare, Award, TrendingUp, Users, Headphones, ChevronRight, Check, X } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const Coach = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Configuration
  const seoConfig = {
    title: 'Coaching Commercial & Formation Sales - FOMOW | Excellence Commerciale',
    description: 'Formation et coaching pour équipes commerciales : prospection, discovery, closing, négociation. Bootcamps intensifs et accompagnement terrain pour performers.',
    path: '/services/coach',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Coaching & Excellence Commerciale",
      "description": "Formation et coaching des équipes commerciales B2B",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Professional Training"
    }
  };

  // Les 3 piliers de la méthode
  const pillars = [
    { 
      num: '01',
      title: 'Méthode', 
      subtitle: 'Maîtriser les fondamentaux',
      desc: 'Avant d\'entrer en action, vos équipes doivent maîtriser les règles du jeu. La vente de valeur est une discipline exigeante, fondée sur des principes clairs.',
      items: ['Frameworks éprouvés (MEDDIC, SPIN, BANT)', 'Process de vente structurés', 'Scripts et séquences optimisés']
    },
    { 
      num: '02',
      title: 'Entraînement', 
      subtitle: 'Affûter les réflexes',
      desc: 'Comme un sportif de haut niveau, vos commerciaux doivent répéter les gestes jusqu\'à ce qu\'ils deviennent des automatismes.',
      items: ['Mises en situation réelles', 'Roleplay sur vos cas clients', 'Feedback immédiat et actionnable']
    },
    { 
      num: '03',
      title: 'Coaching', 
      subtitle: 'Lever les freins',
      desc: 'Au-delà de la technique, le coaching travaille sur le mindset, la confiance et la capacité à performer sous pression.',
      items: ['Accompagnement individuel', 'Gestion du refus et résilience', 'Développement de la posture']
    },
  ];

  // Domaines d'expertise
  const expertises = [
    { 
      icon: Phone, 
      title: 'Prospection', 
      desc: 'Cold calling, séquences email, social selling',
      details: ['Techniques d\'accroche', 'Gestion des barrages', 'Génération de RDV qualifiés']
    },
    { 
      icon: MessageSquare, 
      title: 'Qualification', 
      desc: 'Discovery, MEDDIC, SPIN, BANT',
      details: ['Questions puissantes', 'Écoute active', 'Identification des besoins']
    },
    { 
      icon: Target, 
      title: 'Closing', 
      desc: 'Négociation, objections, engagement',
      details: ['Techniques de closing', 'Gestion des objections', 'Mutual Success Plan']
    },
    { 
      icon: TrendingUp, 
      title: 'Négociation', 
      desc: 'Valeur, prix, conditions',
      details: ['Négociation raisonnée', 'Création de valeur', 'Protection des marges']
    },
    { 
      icon: Users, 
      title: 'Account Management', 
      desc: 'Fidélisation, upsell, cross-sell',
      details: ['Plans de compte', 'Développement du CA client', 'Réduction du churn']
    },
    { 
      icon: Award, 
      title: 'Leadership Sales', 
      desc: 'Management, coaching d\'équipe',
      details: ['Animation d\'équipe', 'Revues de pipeline', 'Coaching de managers']
    },
  ];

  // Formats de programmes
  const programs = [
    {
      title: 'Coaching Individuel',
      desc: 'Accompagnement personnalisé pour développer les compétences de vos meilleurs éléments ou accélérer la montée en puissance.',
      duration: 'Sessions de 1h / semaine',
      format: 'Présentiel ou distanciel',
      ideal: 'Top performers, nouveaux managers, profils à haut potentiel'
    },
    {
      title: 'Formation Collective',
      desc: 'Programmes intensifs pour homogénéiser les pratiques et créer une culture commerciale partagée.',
      duration: 'Modules de 2h à 1 journée',
      format: 'Présentiel recommandé',
      ideal: 'Équipes de 5 à 15 personnes'
    },
    {
      title: 'Bootcamp Intensif',
      desc: 'Immersion totale sur une compétence clé : prospection, closing ou négociation.',
      duration: '2 à 5 jours consécutifs',
      format: 'Présentiel uniquement',
      ideal: 'Lancement d\'équipe, transformation rapide'
    },
  ];

  // Parcours par niveau
  const tracks = [
    {
      level: 'Junior',
      title: 'Fondations',
      desc: 'Pour les commerciaux en début de carrière ou en reconversion.',
      skills: ['Prospection', 'Qualification', 'Pitch', 'Gestion du refus']
    },
    {
      level: 'Confirmé',
      title: 'Performance',
      desc: 'Pour les commerciaux expérimentés qui veulent passer au niveau supérieur.',
      skills: ['Closing avancé', 'Négociation complexe', 'Grands comptes', 'Vente de valeur']
    },
    {
      level: 'Manager',
      title: 'Leadership',
      desc: 'Pour les managers commerciaux qui veulent développer leurs équipes.',
      skills: ['Coaching d\'équipe', 'Revue de pipeline', 'Animation commerciale', 'Gestion de la performance']
    },
  ];

  // Comparaison avec formation classique
  const comparisons = [
    { bad: 'Formations théoriques descendantes', good: 'Apprentissage par la pratique intensive' },
    { bad: 'Contenus génériques et standardisés', good: 'Programmes adaptés à votre contexte et vos deals' },
    { bad: 'Une journée puis plus de suivi', good: 'Accompagnement dans la durée avec mesure des résultats' },
    { bad: 'Formateurs sans expérience terrain récente', good: 'Coachs qui vendent encore au quotidien' },
  ];

  // FAQ
  const faqs = [
    { 
      q: 'Quels formats de coaching proposez-vous ?', 
      a: 'Nous proposons du coaching individuel (1h/semaine), des formations collectives (demi-journée à journée complète) et des bootcamps intensifs (2-5 jours). Le format dépend de vos objectifs et contraintes.' 
    },
    { 
      q: 'Combien de temps faut-il pour voir des résultats ?', 
      a: 'Les premiers changements de comportement sont visibles dès les premières sessions. L\'impact sur les KPIs (taux de conversion, deals signés) se mesure généralement après 2-3 mois d\'accompagnement régulier.' 
    },
    { 
      q: 'Intervenez-vous sur site ou à distance ?', 
      a: 'Les deux. Le présentiel est idéal pour les bootcamps et formations collectives. Le distanciel fonctionne très bien pour le coaching individuel et le suivi régulier.' 
    },
    { 
      q: 'Comment adaptez-vous les programmes à notre contexte ?', 
      a: 'On commence toujours par un diagnostic : compréhension de votre offre, votre marché, votre cycle de vente. Les exercices et roleplay utilisent vos vrais cas clients.' 
    },
    { 
      q: 'Formez-vous aussi les managers commerciaux ?', 
      a: 'Oui, c\'est même essentiel. Un manager qui sait coacher démultiplie l\'impact de la formation. Nous avons des parcours spécifiques pour les managers de premier niveau.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">Formation & Coaching</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Coaching & Excellence Commerciale
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed mb-4">
              La vente est un <span className="text-[#2A41E5] font-semibold">sport de haut niveau</span>. 
              Vos commerciaux méritent un entraînement à la hauteur de vos ambitions.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-lg text-white/60 max-w-2xl mb-10">
              Programmes personnalisés pour développer les compétences, affûter les réflexes et créer une culture commerciale d'excellence.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Contactez-nous
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* La méthode - 3 piliers */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Notre méthode</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Trois piliers pour développer l'excellence
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Chaque accompagnement repose sur ces trois fondamentaux pour créer un impact durable.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-[#2A41E5]/30">
                  <div className="w-14 h-14 rounded-xl bg-[#2A41E5] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-lg">{pillar.num}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pillar.title}</h3>
                  <p className="text-[#2A41E5] font-medium mb-4">{pillar.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed mb-6">{pillar.desc}</p>
                  <ul className="space-y-2">
                    {pillar.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Domaines d'expertise */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos domaines</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 text-center">
              Tout le cycle de vente couvert
            </h2>
            <p className="text-lg text-white/60 text-center max-w-3xl mx-auto mb-16">
              De la prospection au closing, en passant par la négociation et l'account management.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertises.map((exp, idx) => {
              const IconComponent = exp.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-[#2A41E5]/50 transition-all duration-300 group h-full">
                    <div className="flex items-start gap-4 mb-4">
                      <IconComponent className="w-6 h-6 text-[#2A41E5] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{exp.title}</h3>
                        <p className="text-white/60 text-sm">{exp.desc}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 mt-4 pt-4 border-t border-white/10">
                      {exp.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#2A41E5]" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formats de programmes */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Formats</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Des programmes adaptés à vos besoins
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Du coaching individuel aux bootcamps intensifs, nous adaptons le format à vos objectifs.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{program.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{program.desc}</p>
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-3">
                      <Headphones className="w-4 h-4 text-[#2A41E5]" />
                      <span className="text-sm text-gray-700">{program.duration}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-[#2A41E5]" />
                      <span className="text-sm text-gray-700">{program.format}</span>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Idéal pour</p>
                      <p className="text-sm text-[#2A41E5] font-medium">{program.ideal}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Parcours par niveau */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Parcours</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Un accompagnement adapté au niveau
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Juniors, confirmés ou managers : des programmes spécifiques pour chaque profil.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {tracks.map((track, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <div className="bg-[#041C2C] p-6 text-center">
                    <span className="text-[#2A41E5] text-sm font-semibold uppercase tracking-wider">{track.level}</span>
                    <h3 className="text-2xl font-bold text-white mt-2">{track.title}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-6">{track.desc}</p>
                    <div className="space-y-2">
                      {track.skills.map((skill, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-gray-700 text-sm">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Ce qu'on fait / ne fait pas */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Pas une formation classique
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Oubliez les PowerPoint descendants. Notre approche est 100% pratique et orientée résultats.
            </p>
          </AnimatedSection>
          
          <div className="bg-[#F5F5F5] rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2">
              {/* Ce qu'on NE fait PAS */}
              <div className="p-8 md:p-10 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2">
                  <X className="w-6 h-6 text-red-400" />
                  Les formations classiques
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-gray-500">
                        <span className="text-red-400 mt-1">✗</span>
                        {comp.bad}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
              
              {/* Ce qu'on FAIT */}
              <div className="p-8 md:p-10 bg-[#041C2C]">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Notre approche
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-white/80">
                        <span className="text-green-400 mt-1">✓</span>
                        {comp.good}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#2A41E5]/30 transition-colors">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <svg 
                      className={`w-5 h-5 text-[#2A41E5] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à transformer vos équipes commerciales ?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Discutons de vos enjeux. On identifie ensemble les compétences à développer.<br />
              Sans engagement.
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Réserver un échange
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/sparring" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Stratégie & Sparring Partner</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Mentorat stratégique pour dirigeants. Un partenaire exigeant pour prendre du recul et décider vite.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/recruteur" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Acquisition de Talents Stratégiques</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Identification et sélection des meilleurs profils commerciaux pour renforcer vos équipes.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Coach;
